import { useEffect, useState } from "react";

// Tracks PWA install state so the UI can show an install CTA.
//
// - Chromium-based browsers fire `beforeinstallprompt`; we save it and
//   expose a `prompt()` call that surfaces the native install dialog.
// - iOS Safari doesn't fire it at all; we detect iOS and report
//   `kind: "ios"` so the UI can show "Tap Share -> Add to Home Screen"
//   instructions instead.
// - Already-installed apps (display-mode: standalone) report `kind: "none"`.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallState =
  | { kind: "none" } // already installed, or unsupported
  | { kind: "native"; prompt: () => Promise<"accepted" | "dismissed"> }
  | { kind: "ios" };

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari sets `navigator.standalone` outside the standard.
  const navStandalone = (
    window.navigator as Navigator & { standalone?: boolean }
  ).standalone;
  return navStandalone === true;
}

function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  // Exclude Chrome/Firefox on iOS — they don't expose Add-to-Home-Screen
  // in the same way and the share-sheet hint is misleading there.
  const safari = !/CriOS|FxiOS|EdgiOS/.test(ua);
  return iOS && safari;
}

export function useInstallState(): InstallState {
  const [state, setState] = useState<InstallState>(() => {
    if (isStandalone()) return { kind: "none" };
    if (isIosSafari()) return { kind: "ios" };
    return { kind: "none" };
  });

  useEffect(() => {
    if (isStandalone()) return;
    const onPrompt = (e: Event) => {
      e.preventDefault();
      const evt = e as BeforeInstallPromptEvent;
      setState({
        kind: "native",
        prompt: async () => {
          await evt.prompt();
          const choice = await evt.userChoice;
          return choice.outcome;
        },
      });
    };
    const onInstalled = () => setState({ kind: "none" });
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  return state;
}
